/**
 * Rendering Engine — component resolution.
 *
 * A component instance is a reference plus props and slots. This module expands
 * instances into concrete node trees using the document's component
 * definitions, so everything downstream sees plain nodes.
 *
 * Expansion is depth-bounded: a component that instances itself (directly or
 * through a cycle) resolves to nothing rather than hanging the renderer.
 */

const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const asArray = (v) => (Array.isArray(v) ? v : []);

const MAX_DEPTH = 12;

/** Substitute {{prop}} placeholders in strings with the instance's props. */
const fillProps = (value, props) => {
  if (typeof value !== 'string') return value;
  return value.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) => (
    props[key] !== undefined && props[key] !== null ? String(props[key]) : match
  ));
};

/** Deeply apply props to a node's string values, leaving structure intact. */
const applyProps = (node, props) => {
  if (Array.isArray(node)) return node.map((n) => applyProps(n, props));
  if (!isObj(node)) return fillProps(node, props);
  return Object.entries(node).reduce((acc, [key, value]) => ({ ...acc, [key]: applyProps(value, props) }), {});
};

/**
 * Expand every component instance in a node tree.
 *
 * @param {Object[]} nodes
 * @param {Object[]} definitions  the document's `components`.
 * @param {number} [depth]
 * @returns {Object[]} nodes with no `component` instances remaining.
 */
export const expandComponents = (nodes, definitions = [], depth = 0) => {
  if (depth > MAX_DEPTH) return []; // cyclic or pathological nesting: refuse rather than hang
  const byId = new Map(asArray(definitions).filter(isObj).map((d) => [d.id, d]));

  const expandOne = (node, level) => {
    if (!isObj(node)) return [];
    if (level > MAX_DEPTH) return []; // cyclic instancing: refuse rather than recurse forever

    if (node.node === 'component') {
      const def = byId.get(node.component);
      if (!def) return []; // an unresolved reference renders as nothing; the validator reports it
      const props = isObj(node.props) ? node.props : {};
      const slots = isObj(node.slots) ? node.slots : {};

      // The definition may declare variants; a named variant overrides the base.
      const variant = (isObj(def.variants) && node.variant && isObj(def.variants[node.variant])) ? def.variants[node.variant] : {};
      const source = asArray(variant.nodes || def.nodes);

      const children = source.flatMap((child) => {
        // A slot placeholder is replaced by whatever the instance passed in.
        if (child && child.slot && slots[child.slot]) return asArray(slots[child.slot]).flatMap((s) => expandOne(s, level + 1));
        return expandOne(applyProps(child, props), level + 1);
      });

      // The instance becomes a frame so its own layout, layer and geometry survive.
      return [{
        ...def.frame,
        node: 'frame',
        id: node.id,
        role: node.role || def.role || null,
        layer: node.layer || def.layer,
        layout: node.layout || def.layout || (isObj(def.frame) ? def.frame.layout : undefined),
        x: node.x, y: node.y, width: node.width, height: node.height,
        fills: node.fills || def.fills,
        effects: node.effects || def.effects,
        constraints: node.constraints,
        editable: node.editable,
        children,
        instanceOf: node.component,
        variant: node.variant || null,
      }];
    }

    if (asArray(node.children).length) {
      return [{ ...node, children: node.children.flatMap((c) => expandOne(c, level + 1)) }];
    }
    // A frame holds a single `child`, which may itself be an instance.
    if (isObj(node.child)) {
      const [expanded] = expandOne(node.child, level + 1);
      return [{ ...node, child: expanded || null }];
    }
    return [node];
  };

  return asArray(nodes).flatMap((n) => expandOne(n, depth));
};
